import { useEffect, useState } from "react";
import { api } from "../api";
import { useChartSettings } from "../chartSettingsContext";
import type { ChartData } from "../types";
import { PositionChart } from "./PositionChart";

/**
 * Entry and exit charts side by side, for any trade — open or closed.
 *
 * One request, two views: the same candles rendered twice with different
 * visible ranges, so each end of the trade gets a readable window instead of
 * two identical squashed charts. An open trade has no exit yet, so it shows the
 * entry chart plus the run since.
 */
export function TradeCharts({
  tradeId,
  symbol,
  isClosed,
}: {
  tradeId: number;
  symbol: string;
  isClosed: boolean;
}) {
  const [data, setData] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { settings } = useChartSettings();
  const weekly = settings.timeframe === "weekly";

  useEffect(() => {
    let cancelled = false;
    api
      // Weekly needs years of daily bars behind it to fold into enough weeks
      // for a 50-period average.
      .positionChart(tradeId, weekly ? 36 : undefined)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError((e as Error).message));
    return () => {
      cancelled = true;
    };
  }, [tradeId, weekly]);

  if (error) return <div className="hint">Couldn't load charts: {error}</div>;
  if (!data) return <div className="hint">Loading charts…</div>;

  return (
    <div className="chart-pair">
      <PositionChart
        tradeId={tradeId}
        symbol={symbol}
        data={data}
        focus="entry"
        title={`${symbol} — Entry`}
        height={280}
      />
      <PositionChart
        tradeId={tradeId}
        symbol={symbol}
        data={data}
        focus={isClosed ? "exit" : undefined}
        title={`${symbol} — ${isClosed ? "Exit" : "Since entry"}`}
        height={280}
      />
    </div>
  );
}
